/**
 * Practice proposals of the study chat (TANAI-2616): the card reads a proposal and the launcher
 * starts (or resumes) its attempt.
 */
import { createAbortSafeErrorHandler, defaultErrorHandler as commonErrorHandler, createHeaders, getApiUrls } from './serviceConfig';

import type { PracticeAttemptStart, PracticeProposal } from '../types/practiceProposal';

type RequestOptions = {
  signal?: AbortSignal;
};

type StartAttemptParams = {
  intentKey: string;
  newAttempt: boolean;
};

type PracticeProposalServiceConfig = {
  onError?: (error: unknown) => void;
};

type PracticeProposalService = {
  getProposal: (conversationId: string, proposalId: string, options?: RequestOptions) => Promise<PracticeProposal | null>;
  startAttempt: (conversationId: string, proposalId: string, params: StartAttemptParams, options?: RequestOptions) => Promise<PracticeAttemptStart>;
};

/** A request the backend rejected with an HTTP status. */
export class PracticeProposalRequestError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'PracticeProposalRequestError';
    this.status = status;
  }
}

/**
 * A proposal that is not visible (404, also when the flag is off) or an id that is not a UUID
 * (400) can never be started, unlike a server or network error, which is worth retrying.
 */
export function isPracticeProposalNotFound(error: unknown): boolean {
  const status = (error as { status?: unknown } | null)?.status;
  return status === 404 || status === 400;
}

export function createPracticeProposalService(config: PracticeProposalServiceConfig = {}): PracticeProposalService {
  const configuredErrorHandler = config.onError || ((error: unknown) => commonErrorHandler('Practice proposal service', error as Error));
  const handleError = createAbortSafeErrorHandler(configuredErrorHandler) as (error: unknown) => void;

  /** Resolves `null` when the proposal is not visible (404/400): the card then renders nothing. */
  const getProposal = async (conversationId: string, proposalId: string, { signal }: RequestOptions = {}): Promise<PracticeProposal | null> => {
    try {
      const response = await fetch(getApiUrls().chat.practiceProposal(conversationId, proposalId), {
        method: 'GET',
        headers: createHeaders(),
        signal,
      });

      if (response.status === 404 || response.status === 400) return null;
      if (!response.ok) {
        throw new PracticeProposalRequestError(response.status, `Failed to get practice proposal: ${response.status}`);
      }

      return (await response.json()) as PracticeProposal;
    } catch (error) {
      handleError(error);
      throw error;
    }
  };

  const startAttempt = async (
    conversationId: string,
    proposalId: string,
    { intentKey, newAttempt }: StartAttemptParams,
    { signal }: RequestOptions = {}
  ): Promise<PracticeAttemptStart> => {
    try {
      const response = await fetch(getApiUrls().chat.practiceProposalAttempts(conversationId, proposalId), {
        method: 'POST',
        headers: createHeaders(),
        body: JSON.stringify({ intentKey, newAttempt }),
        signal,
      });

      if (!response.ok) {
        throw new PracticeProposalRequestError(response.status, `Failed to start practice attempt: ${response.status}`);
      }

      return (await response.json()) as PracticeAttemptStart;
    } catch (error) {
      handleError(error);
      throw error;
    }
  };

  return { getProposal, startAttempt };
}

export default createPracticeProposalService();
